import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Phone, Mail, MapPin, Clock, MessageCircle, ImagePlus, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { COMPANY, waLink } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_PHOTOS = 5;
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB per file

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Contact() {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]); // [{name, dataUrl, size}]
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });

  const serviceOptions = [
    { id: "renovation", label: t("contact.services_options.renovation") },
    { id: "bathroom", label: t("contact.services_options.bathroom") },
    { id: "solar", label: t("contact.services_options.solar") },
    { id: "ac", label: t("contact.services_options.ac") },
    { id: "painting", label: t("contact.services_options.painting") },
    { id: "ceramica", label: t("contact.services_options.ceramica") },
    { id: "other", label: t("contact.services_options.other") },
  ];

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Combined cap
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(t("contact.photos_too_many"));
      e.target.value = "";
      return;
    }

    const next = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        toast.error(t("contact.photos_invalid"));
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(t("contact.photos_too_big"));
        continue;
      }
      try {
        const dataUrl = await fileToDataUrl(f);
        next.push({ name: f.name, size: f.size, dataUrl });
      } catch {
        // skip
      }
    }
    setPhotos((prev) => [...prev, ...next].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error(lang === "es" ? "Completa nombre, teléfono y servicio" : "Fill name, phone and service");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        locale: lang,
        photos: photos.map((p) => p.dataUrl),
      });
      toast.success(t("contact.success"));
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
      setPhotos([]);
    } catch (err) {
      console.error(err);
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 lg:py-32 bg-[#0A0A0A] text-white relative overflow-hidden"
    >
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-[1px] h-40 bg-[#E11D2E]" />
      <div className="absolute bottom-32 left-10 w-40 h-[1px] bg-[#1F9D40]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: Intro + info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-[#1F9D40]" />
              <span className="text-xs uppercase tracking-[0.22em] text-white/70 font-semibold">
                {t("contact.eyebrow")}
              </span>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.02]">
              {t("contact.title")}
            </h2>
            <p className="mt-5 text-white/70 text-base md:text-lg max-w-md leading-relaxed">
              {t("contact.subtitle")}
            </p>

            <div className="mt-10 space-y-6">
              <InfoRow
                icon={Phone}
                label={t("contact.info.phone_label")}
                value={COMPANY.phone_display}
                href={`tel:${COMPANY.phone_raw}`}
                testId="contact-info-phone"
              />
              <InfoRow
                icon={Mail}
                label={t("contact.info.email_label")}
                value={COMPANY.email}
                href={`mailto:${COMPANY.email}`}
                testId="contact-info-email"
              />
              <InfoRow
                icon={MapPin}
                label={t("contact.info.address_label")}
                value={`${COMPANY.address_line1}\n${COMPANY.address_line2}`}
                href={COMPANY.maps_url}
                testId="contact-info-address"
              />
              <InfoRow
                icon={Clock}
                label={t("contact.info.hours_label")}
                value={t("contact.info.hours_value")}
                testId="contact-info-hours"
              />
            </div>

            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-sm text-white/60">{t("contact.or")}</p>
              <a
                data-testid="contact-whatsapp-link"
                href={waLink("Hola Modolo, quiero pedir un presupuesto.")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-[#25D366] font-bold hover:text-white transition-colors"
              >
                <MessageCircle size={18} /> {t("contact.whatsapp")} · {COMPANY.phone_display}
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 bg-white text-neutral-900 p-6 sm:p-10 lg:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">
                  {t("contact.name")} *
                </label>
                <input
                  data-testid="contact-input-name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="line-input"
                  placeholder={t("contact.name")}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">
                  {t("contact.phone")} *
                </label>
                <input
                  data-testid="contact-input-phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  className="line-input"
                  placeholder="+34 ..."
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1 mt-4">
                  {t("contact.email")}
                </label>
                <input
                  data-testid="contact-input-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  className="line-input"
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1 mt-4">
                  {t("contact.service")} *
                </label>
                <select
                  data-testid="contact-input-service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  required
                  className="line-input bg-white"
                >
                  <option value="" disabled>
                    —
                  </option>
                  {serviceOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 mt-4">
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-1">
                  {t("contact.message")}
                </label>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  className="line-input resize-none"
                  placeholder="..."
                />
              </div>

              {/* Photo upload */}
              <div className="md:col-span-2 mt-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500">
                      {t("contact.photos_label")}
                    </label>
                    <p className="mt-1 text-[11px] text-neutral-500">{t("contact.photos_hint")}</p>
                  </div>
                  <button
                    type="button"
                    data-testid="contact-photos-add"
                    onClick={() => fileRef.current?.click()}
                    disabled={photos.length >= MAX_PHOTOS}
                    className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[#1F9D40] hover:text-[#1F9D40] disabled:opacity-50 disabled:cursor-not-allowed text-neutral-700 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-full transition-colors"
                  >
                    <ImagePlus size={14} />
                    {t("contact.photos_add")} ({photos.length}/{MAX_PHOTOS})
                  </button>
                  <input
                    ref={fileRef}
                    data-testid="contact-photos-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onPickPhotos}
                    className="hidden"
                  />
                </div>

                {photos.length > 0 && (
                  <div
                    data-testid="contact-photos-preview"
                    className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3"
                  >
                    {photos.map((p, i) => (
                      <div
                        key={`${p.name}-${i}`}
                        className="group relative aspect-square overflow-hidden border border-neutral-200"
                      >
                        <img src={p.dataUrl} alt={p.name} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          data-testid={`contact-photos-remove-${i}`}
                          onClick={() => removePhoto(i)}
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-[#E11D2E] text-white flex items-center justify-center transition-colors"
                          aria-label={t("contact.photos_remove")}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="mt-10 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#1F9D40] hover:bg-[#177d33] disabled:opacity-70 disabled:cursor-not-allowed text-white font-bold px-10 py-5 rounded-full text-sm tracking-wide transition-all shadow-lg shadow-green-900/10 hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> {t("contact.sending")}
                </>
              ) : (
                <>
                  <Send size={16} /> {t("contact.submit")}
                </>
              )}
            </button>

            <p className="mt-5 text-xs text-neutral-500">
              CIF {COMPANY.cif} · {COMPANY.address_line1}, {COMPANY.address_line2}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value, href, testId }) {
  const content = (
    <div data-testid={testId} className="flex items-start gap-4 group">
      <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#1F9D40] transition-colors">
        <Icon size={18} className="text-[#1F9D40]" strokeWidth={2.2} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">{label}</div>
        <div className="mt-1 text-sm md:text-base text-white whitespace-pre-line break-words">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block">
      {content}
    </a>
  ) : (
    content
  );
}
